import { useCallback } from "react";

import type { RemovePlayerHandler } from "../types/types";

/**
 * Props for useSidebarInteraction hook
 */
interface UseSidebarInteractionProps {
  /**
   * Callback invoked when a drag operation starts from the sidebar
   */
  onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /**
   * Callback invoked when a drag operation ends
   */
  onGlobalDragEnd?: () => void;
  /**
   * Callback invoked when a player should be removed from the Kanban board
   */
  onRemovePlayerFromBoard?: RemovePlayerHandler;
  /**
   * Callback invoked to trigger auto-scroll when dragging near screen edges
   */
  onAutoScroll?: (dragX: number) => void;
}

/**
 * Hook that handles user interaction events for drag, click, and scroll operations in the sidebar
 * @param root0 - The hook props
 * @param root0.onGlobalDragStart - Callback invoked when a drag operation starts, receives drag data and event information
 * @param root0.onGlobalDragEnd - Callback invoked when a drag operation completes
 * @param root0.onRemovePlayerFromBoard - Callback invoked to remove a player from the Kanban board, receives player data
 * @param root0.onAutoScroll - Callback invoked to trigger auto-scroll when dragging near screen edges, receives horizontal drag position
 * @returns Object with memoized handlers for player click, drag start/end, remove, and auto-scroll events
 */
const useSidebarInteraction = ({
  onGlobalDragStart,
  onGlobalDragEnd,
  onRemovePlayerFromBoard,
  onAutoScroll,
}: UseSidebarInteractionProps) => {
  /*
   * A DELIBERATE no-op, and no longer the add affordance (SE-7070).
   *
   * This predates the add-players drawer — it comes from the legacy player
   * rail, where a row press was meant to open a player detail view and never
   * got built. When SE-5490 reused that rail's row inside the drawer, the
   * press came with it, so the drawer's Watchlists and My Roster tabs offered a
   * click that reached this stub and nothing else. Jake reported the dead end:
   * he filtered to a watchlist and could not select a player, while Suggested —
   * one tab away — had a working Add button.
   *
   * SE-6945/6951/6952 fixed that properly, by giving every pool row a real
   * `+ Add` wired to the pipeline mutation. So this no longer has a job to do
   * on the add path, and it is left inert rather than pointed at the mutation:
   * a whole-row press that adds would fight the drag the same row owns.
   *
   * What remains is the ORIGINAL intent — opening a player's detail drawer on
   * press. That is a product decision and a separate piece of work, explicitly
   * out of scope for SE-7070; `MainContainer` already holds a real
   * `handlePlayerClick` that would satisfy it if the prop were threaded down.
   * Until that call is made this stays silent instead of logging on every press
   * in production.
   */
  const handlePlayerClick = useCallback((_playerId: string) => undefined, []);

  const handleGlobalDragStart = useCallback(
    (dragData?: unknown, event?: unknown) => {
      onGlobalDragStart?.(dragData, event);
    },
    [onGlobalDragStart]
  );

  const handleGlobalDragEnd = useCallback(() => {
    onGlobalDragEnd?.();
  }, [onGlobalDragEnd]);

  const handleRemovePlayerFromBoard = useCallback<RemovePlayerHandler>(
    playerData => {
      onRemovePlayerFromBoard?.(playerData);
    },
    [onRemovePlayerFromBoard]
  );

  const handleAutoScroll = useCallback(
    (dragX: number) => {
      onAutoScroll?.(dragX);
    },
    [onAutoScroll]
  );

  return {
    // Handlers
    handlePlayerClick,
    handleGlobalDragStart,
    handleGlobalDragEnd,
    handleRemovePlayerFromBoard,
    handleAutoScroll,
  };
};

export default useSidebarInteraction;
